import { cn } from '@/lib/utils';
import { Plus, Trash2 } from 'lucide-react';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";
const selectCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">{label}</label>
    {children}
  </div>
);

const YesNo = ({ label, value, onChange }) => (
  <div>
    <p className="text-xs text-muted-foreground mb-1.5">{label}</p>
    <div className="flex items-center gap-4">
      {['No', 'Yes'].map(opt => (
        <button key={opt} type="button" onClick={() => onChange(opt)} className="flex items-center gap-2 cursor-pointer">
          <div className={cn(
            "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
            value === opt ? 'border-primary bg-primary' : 'border-border'
          )}>
            {value === opt && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
          </div>
          <span className="text-sm text-foreground">{opt}</span>
        </button>
      ))}
    </div>
  </div>
);

// Calculate employment duration from mm/yyyy strings
const calcDuration = (from, to, isPresent) => {
  if (!from) return null;
  const [fm, fy] = from.split('/').map(Number);
  if (!fm || !fy) return null;
  const toDate = isPresent ? new Date() : (() => { const [tm, ty] = (to || '').split('/').map(Number); return tm && ty ? new Date(ty, tm - 1) : null; })();
  if (!toDate) return null;
  const fromDate = new Date(fy, fm - 1);
  const months = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
  if (months < 0) return null;
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  return yrs > 0 ? `${yrs}yr ${mos}mo` : `${mos}mo`;
};

export const emptyEmployer = () => ({
  id: Date.now() + Math.random(),
  name: '', address: '', city: '', state: '', zip: '',
  phone: '', fax: '', email: '', position: '',
  date_from: '', date_to: '', is_present: false, reason: '',
  dot_regulated: 'Yes', safety_sensitive: 'Yes', gaps: '',
});

const parseMMYYYY = (s) => {
  const [m, y] = (s || '').split('/').map(Number);
  return m && y ? new Date(y, m - 1) : null;
};

export default function StepEmploymentHistory({ employers, setEmployers, errors = {} }) {
  const update = (id, k, v) => setEmployers(prev => prev.map(e => e.id === id ? { ...e, [k]: v } : e));
  const remove = (id) => setEmployers(prev => prev.filter(e => e.id !== id));

  // Live years-covered calculation
  const dates = employers.map(e => parseMMYYYY(e.date_from)).filter(Boolean);
  const earliest = dates.length ? new Date(Math.min(...dates)) : null;
  const yearsCovered = earliest ? (Date.now() - earliest) / (1000 * 60 * 60 * 24 * 365.25) : 0;
  const pct = Math.min((yearsCovered / 7) * 100, 100);
  const met = yearsCovered >= 7;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-space font-semibold text-foreground text-base">Employment Record</h3>
        <p className="text-xs text-muted-foreground mt-0.5">List all employers for the previous 3 years. If a driver, add an additional 7 years.</p>
      </div>

      <div className="px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs leading-relaxed">
        List ALL employers for the previous 3 years and an additional 7 years if you were employed as a DRIVER. You must investigate employment for the preceding 3 years per 49 CFR 391.23.
      </div>

      {/* 7-year coverage tracker */}
      <div className={cn("px-3 py-2.5 rounded-lg border text-xs space-y-1.5", met ? "bg-green-500/10 border-green-500/20" : "bg-amber-500/10 border-amber-500/20")}>
        <div className="flex items-center justify-between">
          <span className={met ? "text-green-400 font-semibold" : "text-amber-400 font-semibold"}>
            {met ? `✓ 7-year requirement met (${yearsCovered.toFixed(1)} yrs covered)` : `${yearsCovered.toFixed(1)} / 7 years covered — add earlier employers`}
          </span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className={cn("h-full rounded-full transition-all", met ? "bg-green-400" : "bg-amber-400")} style={{ width: `${pct}%` }} />
        </div>
      </div>
      {errors.employment_coverage && (
        <p className="text-xs text-red-400">{errors.employment_coverage}</p>
      )}

      {employers.map((emp, i) => (
        <div key={emp.id} className="rounded-xl border border-border bg-muted/40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-primary uppercase tracking-wider">Employer {i + 1}</p>
            {employers.length > 1 && (
              <button type="button" onClick={() => remove(emp.id)} className="text-red-400 hover:text-red-300 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Employer Name *">
              <input value={emp.name} onChange={e => update(emp.id, 'name', e.target.value)} className={inputCls} placeholder="Company name" />
            </Field>
            <Field label="Contact Person">
              <input value={emp.contact || ''} onChange={e => update(emp.id, 'contact', e.target.value)} className={inputCls} placeholder="Contact name" />
            </Field>
            <div className="sm:col-span-2">
              <TrimbleAddressAutocomplete
                value={emp.address}
                onChange={e => update(emp.id, 'address', e.target.value)}
                onSelect={data => {
                  update(emp.id, 'address', data.street || emp.address);
                  if (data.city) update(emp.id, 'city', data.city);
                  if (data.state) update(emp.id, 'state', data.state);
                  if (data.zip) update(emp.id, 'zip', data.zip);
                }}
                label="Address"
              />
            </div>
            <Field label="City">
              <input value={emp.city} onChange={e => update(emp.id, 'city', e.target.value)} className={inputCls} />
            </Field>
            <Field label="State">
              <select value={emp.state} onChange={e => update(emp.id, 'state', e.target.value)} className={selectCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Zip">
              <input value={emp.zip} onChange={e => update(emp.id, 'zip', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Phone">
              <input value={emp.phone} onChange={e => update(emp.id, 'phone', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Fax">
              <input value={emp.fax} onChange={e => update(emp.id, 'fax', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Email">
              <input value={emp.email} onChange={e => update(emp.id, 'email', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Position Held">
              <input value={emp.position} onChange={e => update(emp.id, 'position', e.target.value)} className={inputCls} placeholder="e.g. CDL Driver" />
            </Field>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Date From (mm/yyyy)</label>
              <input value={emp.date_from} onChange={e => update(emp.id, 'date_from', e.target.value)} className={inputCls} placeholder="03/2022" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="text-xs text-muted-foreground">Date To (mm/yyyy)</label>
                <button type="button" onClick={() => update(emp.id, 'is_present', !emp.is_present)} className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  <div className={cn("w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors", emp.is_present ? "bg-primary border-primary" : "border-border")}>
                    {emp.is_present && <div className="w-2 h-2 bg-primary-foreground rounded-sm" />}
                  </div>
                  Present
                </button>
              </div>
              {!emp.is_present && (
                <input value={emp.date_to} onChange={e => update(emp.id, 'date_to', e.target.value)} className={inputCls} placeholder="02/2025" />
              )}
              {(() => { const dur = calcDuration(emp.date_from, emp.date_to, emp.is_present); return dur ? <p className="text-xs text-primary mt-1">Duration: {dur}</p> : null; })()}
            </div>
            <Field label="Reason For Leaving">
              <input value={emp.reason} onChange={e => update(emp.id, 'reason', e.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="space-y-2 pt-2 border-t border-border">
            <YesNo
              label="Were you subject to DOT/FMCSA regulations? (If driver, choose Yes)"
              value={emp.dot_regulated}
              onChange={v => update(emp.id, 'dot_regulated', v)}
            />
            <YesNo
              label="Was your job a safety-sensitive function subject to 49 CFR Part 40? (If driver, choose Yes)"
              value={emp.safety_sensitive}
              onChange={v => update(emp.id, 'safety_sensitive', v)}
            />
            <div>
              <p className="text-xs text-muted-foreground mb-1">Explain any gaps in employment (if &gt; 1 month):</p>
              <textarea
                value={emp.gaps}
                onChange={e => update(emp.id, 'gaps', e.target.value)}
                rows={2}
                className={inputCls + ' resize-none'}
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => setEmployers(prev => [...prev, emptyEmployer()])}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors w-full justify-center"
      >
        <Plus className="w-3.5 h-3.5" /> Add Another Employer
      </button>
    </div>
  );
}